import { jsx as _jsx } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import MuiAvatar, { avatarClasses } from '@mui/material/Avatar';
import * as React from 'react';
import { getReadableTextColor } from '../color';
import { getIcon } from '../icon';
import { usePaletteColor } from '../theme';
import { deepMerge, makeEnum, withVersion } from '../utils';
export const AvatarSize = makeEnum(18, 24, 32, 40, 60, 80, 120, 160);
const AVATAR_FILL = 'components.avatar.fill';
const AvatarImpl = React.forwardRef((props, ref) => {
    const { size = 40, color = AVATAR_FILL, variant = 'circular', active = false, title, children: _children, icon, src, disabled = false, disabledInitials = false, ...muiProps } = props;
    const { components } = useTheme();
    const contentColor = usePaletteColor('components.content.fill');
    const bgColor = usePaletteColor(disabled ? AVATAR_FILL : color);
    const textSecondaryColor = usePaletteColor('text.secondary');
    const outlineColor = usePaletteColor('components.avatar.active-border');
    const textDisabledColor = usePaletteColor('text.disabled');
    const preferredTextColor = disabled
        ? textDisabledColor
        : textSecondaryColor;
    const textColor = (() => {
        if (bgColor && preferredTextColor) {
            return getReadableTextColor(bgColor, preferredTextColor);
        }
        return preferredTextColor;
    })();
    const font = (() => {
        switch (size) {
            case 160:
            case 120:
            case 80:
            case 60:
            case 40:
                return components?.avatar?.['initials-lg'];
            case 32:
            case 24:
                return components?.avatar?.['initials-md'];
            case 18:
                return components?.avatar?.['initials-sm'];
        }
    })();
    const iconSize = (() => {
        switch (size) {
            case 160:
            case 120:
            case 80:
                return 'large';
            case 60:
            case 40:
            case 32:
                return 'medium';
            case 24:
            case 18:
                return 'small';
        }
    })();
    const Icon = icon ? getIcon(icon) : null;
    const children = Icon ? (_jsx(Icon, { color: () => textColor, fontSize: iconSize })) : (_children);
    return (_jsx(MuiAvatar, { ref: ref, "data-role": "avatar", sx: deepMerge({}, muiProps.sx ?? {}, {
            bgcolor: bgColor,
            color: textColor,
            width: size,
            height: size,
            cursor: props.onClick ? 'pointer' : undefined,
            borderColor: contentColor,
            borderStyle: 'solid',
            borderWidth: active ? 4 : 0,
            outlineWidth: active ? 4 : 0,
            outlineColor,
            outlineStyle: 'solid',
            [`& .${avatarClasses.img}`]: {
                filter: disabled ? 'grayscale(1)' : undefined
            }
        }), variant: variant, src: src, ...muiProps, children: children ?? (_jsx("span", { style: font, children: disabledInitials ? title : getInitials(title) })) }));
});
export const Avatar = withVersion(AvatarImpl, '1.2.0');
export const AvatarColor = (props) => {
    const borderColor = usePaletteColor('divider');
    return (_jsx(MuiAvatar, { ...props, sx: {
            ...props.sx,
            bgcolor: props.color,
            width: props.size ?? 18,
            height: props.size ?? 18,
            borderColor,
            borderStyle: 'solid',
            borderWidth: 1
        }, children: ' ' }));
};
function getInitials(name) {
    const parts = name?.split(' ') ?? [];
    if (parts.length === 1) {
        return parts[0]?.toUpperCase().slice(0, 2);
    }
    return parts
        .map((word) => word[0]?.replace(/[^a-zA-Z]/g, '').toUpperCase())
        .filter(Boolean)
        .filter((_, i, arr) => i === 0 || i === arr.length - 1)
        .join('');
}
